
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { StableTabContainer } from './StableTabContainer';
import { PreRenderedTabContent } from './PreRenderedTabContent';
import { BasicInfoForm } from './BasicInfoForm';
import { HtmlEditor } from './HtmlEditor';
import { VariablesEditor } from './VariablesEditor';
import { SystemVariables } from './SystemVariables';
import { CategoryManager } from './CategoryManager';
import { ScheduleManager } from './ScheduleManager';

interface EmailEditorTabsProps {
  editedTemplate: any;
  previewHtml: string;
  variablesJson: string;
  variablesValid: boolean;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | string) => void;
  onSelectChange: (name: string, value: string) => void;
  onVariablesChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  getPreviewHtmlWithExampleValues: (html: string) => string;
}

export function EmailEditorTabs({
  editedTemplate,
  previewHtml,
  variablesJson,
  variablesValid,
  onInputChange,
  onSelectChange,
  onVariablesChange,
  getPreviewHtmlWithExampleValues
}: EmailEditorTabsProps) {
  const [activeTab, setActiveTab] = useState('edit');
  const [tabsInitialized, setTabsInitialized] = useState({
    edit: true,
    'html-editor': true, // Initialize both frequently used tabs at start
    variables: true,     // Initialize both frequently used tabs at start
    'sys-variables': false,
    categories: false,
    schedule: false
  });
  
  // Controlled tab switching logic to prevent layout shifts
  const handleTabChange = useCallback((value: string) => {
    // Set the active tab immediately for better UX
    setActiveTab(value);
    
    // Mark this tab as initialized
    setTabsInitialized(prev => ({
      ...prev,
      [value]: true
    }));
  }, []);
  
  // Initialize all tabs when component mounts to prevent issues
  useEffect(() => {
    console.log("EmailEditorTabs mounted with editedTemplate:", !!editedTemplate);
    // Initialize all tabs after a short delay
    const timer = setTimeout(() => {
      setTabsInitialized({
        edit: true,
        'html-editor': true,
        variables: true,
        'sys-variables': true,
        categories: true,
        schedule: true
      });
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Memoize each tab's content to prevent excessive re-renders
  const editTab = useMemo(() => (
    <BasicInfoForm 
      template={editedTemplate} 
      onInputChange={onInputChange}
      onSelectChange={onSelectChange} 
    />
  ), [editedTemplate, onInputChange, onSelectChange]);
  
  const htmlEditorTab = useMemo(() => (
    <HtmlEditor 
      htmlContent={editedTemplate.html_content} 
      onChange={onInputChange} 
      previewHtml={previewHtml}
      getPreviewHtmlWithExampleValues={getPreviewHtmlWithExampleValues}
    />
  ), [editedTemplate.html_content, onInputChange, previewHtml, getPreviewHtmlWithExampleValues]);
  
  const variablesTab = useMemo(() => (
    <VariablesEditor 
      variablesJson={variablesJson} 
      variablesValid={variablesValid} 
      onChange={onVariablesChange} 
    />
  ), [variablesJson, variablesValid, onVariablesChange]);

  // Loading indicator component for lazy-loaded tabs
  const LoadingIndicator = () => (
    <div className="flex items-center justify-center p-8">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
    </div>
  );

  return (
    <StableTabContainer height="auto" style={{ minHeight: "720px" }} className="overflow-visible">
      <Tabs 
        value={activeTab} 
        onValueChange={handleTabChange}
        className="h-full flex flex-col"
      >
        <div className="sticky top-0 bg-background z-10 pb-2">
          <TabsList className="mb-4">
            <TabsTrigger value="edit">Basic Info</TabsTrigger>
            <TabsTrigger value="html-editor">HTML Editor</TabsTrigger>
            <TabsTrigger value="variables">Variables</TabsTrigger>
            <TabsTrigger value="sys-variables">System Variables</TabsTrigger>
            <TabsTrigger value="categories">Categories</TabsTrigger>
            <TabsTrigger value="schedule">Email Schedule</TabsTrigger>
          </TabsList>
        </div>
        
        <div className="relative flex-1 overflow-y-auto">
          <PreRenderedTabContent value="edit" activeTab={activeTab}>
            <div className="overflow-y-auto">
              {editTab}
            </div>
          </PreRenderedTabContent>
          
          <PreRenderedTabContent value="html-editor" activeTab={activeTab}>
            {htmlEditorTab}
          </PreRenderedTabContent>
          
          <PreRenderedTabContent value="variables" activeTab={activeTab}>
            <div className="overflow-y-auto">
              {variablesTab}
            </div>
          </PreRenderedTabContent>

          <PreRenderedTabContent value="sys-variables" activeTab={activeTab}>
            <div className="overflow-y-auto">
              {tabsInitialized['sys-variables'] && <SystemVariables />}
            </div>
          </PreRenderedTabContent>

          {/* Lazy load heavy components */}
          <PreRenderedTabContent value="categories" activeTab={activeTab}>
            <div className="overflow-y-auto">
              {activeTab === 'categories' && !tabsInitialized.categories ? (
                <LoadingIndicator />
              ) : tabsInitialized.categories ? <CategoryManager /> : null}
            </div>
          </PreRenderedTabContent>

          <PreRenderedTabContent value="schedule" activeTab={activeTab}>
            <div className="overflow-y-auto">
              {activeTab === 'schedule' && !tabsInitialized.schedule ? (
                <LoadingIndicator />
              ) : tabsInitialized.schedule ? <ScheduleManager /> : null}
            </div>
          </PreRenderedTabContent>
        </div>
      </Tabs>
    </StableTabContainer>
  );
}
